import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/ApiService';

function fmtDate(v) {
  if (!v) return '';
  return new Date(v).toLocaleString();
}

function fmtDuration(s) {
  if (!s) return '';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const TIMELINE_COLORS = {
  call:      '#6366f1',
  call_note: '#0ea5e9',
  note:      '#0ea5e9',
  task:      '#f59e0b',
  lead:      '#10b981',
};

// ── Customer List ────────────────────────────────────────────────────────────

function CustomerList({ onSelect, onDial }) {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', company: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = search.trim()
        ? await api.crmSearch(search.trim())
        : await api.getCrmCustomers();
      setCustomers(Array.isArray(data) ? data : data.customers || []);
    } catch (e) {
      console.error('Failed to load customers', e);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const saveNew = async () => {
    if (!form.name.trim() && !form.phone.trim()) {
      alert('Enter at least a name or phone number.');
      return;
    }
    setSaving(true);
    try {
      await api.createCrmCustomer(form);
      setShowForm(false);
      setForm({ name: '', phone: '', email: '', company: '', notes: '' });
      await load();
    } catch (e) {
      console.error('Failed to create customer', e);
      alert('Failed to create customer.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Search + add */}
      <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: '10px', flexShrink: 0 }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, phone, email, company…"
          style={{
            flex: 1, padding: '8px 12px', borderRadius: '8px',
            border: '1px solid #d1d5db', fontSize: '14px',
          }}
        />
        <button
          onClick={() => setShowForm(s => !s)}
          style={{
            background: '#6366f1', color: '#fff', border: 'none',
            borderRadius: '8px', padding: '8px 16px', fontSize: '13px',
            cursor: 'pointer', fontWeight: 600,
          }}
        >
          {showForm ? 'Close' : '+ New'}
        </button>
      </div>

      {showForm && (
        <div style={{
          padding: '14px 16px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb',
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', flexShrink: 0,
        }}>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name" style={inputStyle} />
          <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="Phone number" style={inputStyle} />
          <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Email" style={inputStyle} />
          <input value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} placeholder="Company" style={inputStyle} />
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes" rows={2} style={{ ...inputStyle, gridColumn: 'span 2', resize: 'vertical' }} />
          <button onClick={saveNew} disabled={saving} style={btnStyle('6366f1')}>
            {saving ? 'Saving…' : 'Create Customer'}
          </button>
        </div>
      )}

      {/* List */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {loading && <div style={emptyStyle}>Loading…</div>}
        {!loading && customers.length === 0 && (
          <div style={emptyStyle}>{search ? 'No results.' : 'No customers yet.'}</div>
        )}
        {customers.map(c => (
          <div
            key={c.id}
            onClick={() => onSelect(c)}
            style={{
              padding: '13px 16px', borderBottom: '1px solid #f3f4f6',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px',
              transition: 'background 0.1s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
            onMouseLeave={e => e.currentTarget.style.background = ''}
          >
            <div style={{
              width: 38, height: 38, borderRadius: '50%',
              background: '#6366f1', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '15px', fontWeight: 700, flexShrink: 0,
            }}>
              {(c.name || c.phone || '?')[0].toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: '#111827', fontSize: '14px' }}>{c.name || '(no name)'}</div>
              <div style={{ color: '#6b7280', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {[c.phone, c.email, c.company].filter(Boolean).join(' · ')}
              </div>
            </div>
            {c.phone && onDial && (
              <button
                onClick={e => { e.stopPropagation(); onDial(c.phone); }}
                style={{
                  background: '#10b981', color: '#fff', border: 'none',
                  borderRadius: '8px', padding: '5px 12px', fontSize: '12px',
                  cursor: 'pointer', fontWeight: 600, flexShrink: 0,
                }}
              >
                Call
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Customer Detail ──────────────────────────────────────────────────────────

function CustomerDetail({ customer: initial, onBack, onDial }) {
  const [customer, setCustomer] = useState(initial);
  const [timeline, setTimeline] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...initial });
  const [activeTab, setActiveTab] = useState('timeline');
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDue, setNewTaskDue] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [tl, tk] = await Promise.all([
        api.getCustomerTimeline(customer.id),
        api.getCrmTasks(customer.id),
      ]);
      setTimeline(tl || []);
      setTasks(tk || []);
    } catch (e) {
      console.error('Failed to load customer detail', e);
    } finally {
      setLoading(false);
    }
  }, [customer.id]);

  useEffect(() => { refresh(); }, [refresh]);

  const saveEdit = async () => {
    try {
      const updated = await api.updateCrmCustomer(customer.id, form);
      setCustomer(prev => ({ ...prev, ...form, ...(updated?.customer || updated || {}) }));
      setEditing(false);
    } catch (e) {
      console.error('Failed to update customer', e);
      alert('Failed to update customer.');
    }
  };

  const saveNote = async () => {
    const note = newNote.trim();
    if (!note) return;
    setSavingNote(true);
    try {
      await api.saveCrmNote({ customer_id: customer.id, note });
      setNewNote('');
      await refresh();
    } catch (e) {
      console.error('Failed to save note', e);
    } finally {
      setSavingNote(false);
    }
  };

  const addTask = async () => {
    const title = newTaskTitle.trim();
    if (!title) return;
    try {
      await api.createCrmTask({ customer_id: customer.id, title, due_date: newTaskDue || null });
      setNewTaskTitle('');
      setNewTaskDue('');
      await refresh();
    } catch (e) {
      console.error('Failed to add task', e);
    }
  };

  const completeTask = async (task) => {
    try {
      await api.updateCrmTask(task.id, { status: 'done' });
      await refresh();
    } catch (e) {
      console.error('Failed to complete task', e);
    }
  };

  const openTasks = tasks.filter(t => t.status !== 'done');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{
        padding: '14px 20px', borderBottom: '1px solid #e5e7eb',
        background: '#f8fafc', flexShrink: 0,
        display: 'flex', alignItems: 'center', gap: '14px',
      }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '20px', padding: 0 }}>
          ←
        </button>
        <div style={{
          width: 44, height: 44, borderRadius: '50%',
          background: '#6366f1', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '18px', fontWeight: 700, flexShrink: 0,
        }}>
          {(customer.name || customer.phone || '?')[0].toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '17px', color: '#111827' }}>{customer.name || '(no name)'}</div>
          <div style={{ color: '#6b7280', fontSize: '13px' }}>
            {[customer.phone, customer.email, customer.company].filter(Boolean).join(' · ')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {customer.phone && onDial && (
            <button onClick={() => onDial(customer.phone)} style={btnStyle('10b981')}>Call</button>
          )}
          <button onClick={() => setEditing(s => !s)} style={btnStyle('6366f1')}>{editing ? 'Cancel' : 'Edit'}</button>
        </div>
      </div>

      {/* Edit form */}
      {editing && (
        <div style={{
          padding: '14px 20px', background: '#fafafa', borderBottom: '1px solid #e5e7eb',
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', flexShrink: 0,
        }}>
          <input value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name" style={inputStyle} />
          <input value={form.phone || ''} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="Phone" style={inputStyle} />
          <input value={form.email || ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Email" style={inputStyle} />
          <input value={form.company || form.company_name || ''} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} placeholder="Company" style={inputStyle} />
          <textarea value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes" rows={2} style={{ ...inputStyle, gridColumn: 'span 2', resize: 'vertical' }} />
          <button onClick={saveEdit} style={btnStyle('6366f1')}>Save Changes</button>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
        {[
          { id: 'timeline', label: 'Timeline' },
          { id: 'tasks', label: `Tasks${openTasks.length > 0 ? ` (${openTasks.length})` : ''}` },
          { id: 'notes', label: 'Add Note' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 20px', border: 'none', background: 'none',
              borderBottom: activeTab === tab.id ? '2px solid #6366f1' : '2px solid transparent',
              color: activeTab === tab.id ? '#6366f1' : '#6b7280',
              fontWeight: activeTab === tab.id ? 600 : 400,
              cursor: 'pointer', fontSize: '13px', transition: 'all 0.15s',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>

        {/* TIMELINE */}
        {activeTab === 'timeline' && (
          <div>
            {loading && <div style={emptyStyle}>Loading…</div>}
            {!loading && timeline.length === 0 && <div style={emptyStyle}>No interactions yet.</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {timeline.map((item, i) => (
                <div key={i} style={{
                  padding: '12px 14px', background: '#f8fafc', borderRadius: '10px',
                  borderLeft: `3px solid ${TIMELINE_COLORS[item.type] || '#6b7280'}`,
                  display: 'flex', gap: '12px',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ fontWeight: 600, fontSize: '13px', color: '#374151', textTransform: 'capitalize' }}>
                        {item.type === 'call_note' ? 'Call Note' : item.type}
                        {item.type === 'call' && item.direction ? ` · ${item.direction}` : ''}
                      </span>
                      <span style={{ color: '#9ca3af', fontSize: '11px' }}>
                        {timeAgo(item.created_at || item.call_start)}
                      </span>
                    </div>
                    {item.type === 'call' && (
                      <div style={{ color: '#6b7280', fontSize: '12px' }}>
                        {item.status || 'unknown'}
                        {item.duration ? ` · ${fmtDuration(item.duration)}` : ''}
                        {item.caller_number ? ` · From ${item.caller_number}` : ''}
                        {item.callee_number ? ` · To ${item.callee_number}` : ''}
                      </div>
                    )}
                    {(item.type === 'note' || item.type === 'call_note') && (
                      <div style={{ color: '#374151', fontSize: '13px', lineHeight: 1.5 }}>{item.note}</div>
                    )}
                    {item.type === 'task' && (
                      <div style={{ color: '#374151', fontSize: '13px' }}>
                        {item.title}
                        {item.status === 'done' ? (
                          <span style={{ color: '#10b981', marginLeft: 8, fontSize: '11px' }}>Completed</span>
                        ) : item.due_date ? (
                          <span style={{ color: '#9ca3af', marginLeft: 8, fontSize: '11px' }}>Due: {fmtDate(item.due_date)}</span>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TASKS */}
        {activeTab === 'tasks' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {/* Open tasks */}
            {openTasks.length === 0 && !loading && (
              <div style={{ color: '#9ca3af', textAlign: 'center', padding: '12px 0' }}>No open tasks.</div>
            )}
            {openTasks.map(task => (
              <div key={task.id} style={{
                display: 'flex', alignItems: 'flex-start', gap: '10px',
                padding: '12px 14px', background: '#fefce8', borderRadius: '10px',
                border: '1px solid #fde68a',
              }}>
                <button
                  onClick={() => completeTask(task)}
                  style={{
                    width: 22, height: 22, borderRadius: '50%', border: '2px solid #d1d5db',
                    background: '#fff', cursor: 'pointer', flexShrink: 0, marginTop: 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                  }}
                  title="Mark done"
                >
                  <span style={{ fontSize: '11px', color: '#6b7280' }}>✓</span>
                </button>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, color: '#111827' }}>{task.title}</div>
                  {task.due_date && (
                    <div style={{ color: '#9ca3af', fontSize: '12px', marginTop: 2 }}>Due: {fmtDate(task.due_date)}</div>
                  )}
                </div>
              </div>
            ))}

            {/* Add task */}
            <div style={{ padding: '12px 14px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e5e7eb' }}>
              <div style={{ fontWeight: 600, marginBottom: '10px', fontSize: '13px', color: '#374151' }}>New Task</div>
              <input
                value={newTaskTitle}
                onChange={e => setNewTaskTitle(e.target.value)}
                placeholder="Task title…"
                style={{ ...inputStyle, marginBottom: '8px' }}
                onKeyDown={e => e.key === 'Enter' && addTask()}
              />
              <input
                type="datetime-local"
                value={newTaskDue}
                onChange={e => setNewTaskDue(e.target.value)}
                style={{ ...inputStyle, marginBottom: '10px' }}
              />
              <button onClick={addTask} disabled={!newTaskTitle.trim()} style={btnStyle('6366f1')}>
                + Add Task
              </button>
            </div>

            {/* Completed */}
            {tasks.filter(t => t.status === 'done').length > 0 && (
              <>
                <div style={{ color: '#6b7280', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 8 }}>Completed</div>
                {tasks.filter(t => t.status === 'done').map(task => (
                  <div key={task.id} style={{
                    padding: '10px 14px', background: '#f8fafc', borderRadius: '10px',
                    color: '#9ca3af', textDecoration: 'line-through', fontSize: '13px',
                  }}>
                    {task.title}
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* ADD NOTE */}
        {activeTab === 'notes' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <textarea
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
              placeholder="Write a note about this customer…"
              rows={5}
              style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
            />
            <button
              onClick={saveNote}
              disabled={!newNote.trim() || savingNote}
              style={btnStyle('6366f1')}
            >
              {savingNote ? 'Saving…' : 'Save Note'}
            </button>

            {/* Recent notes from timeline */}
            <div style={{ marginTop: 8 }}>
              <div style={{ fontWeight: 600, color: '#374151', marginBottom: 10, fontSize: '13px' }}>Previous Notes</div>
              {timeline.filter(t => t.type === 'note' || t.type === 'call_note').length === 0 && (
                <div style={{ color: '#9ca3af', fontSize: '12px' }}>No notes yet.</div>
              )}
              {timeline
                .filter(t => t.type === 'note' || t.type === 'call_note')
                .map((item, i) => (
                  <div key={i} style={{
                    padding: '10px 12px', background: '#f0f9ff', borderRadius: '10px',
                    borderLeft: '3px solid #0ea5e9', marginBottom: 8,
                  }}>
                    <div style={{ color: '#374151', fontSize: '13px', lineHeight: 1.5 }}>{item.note}</div>
                    <div style={{ color: '#9ca3af', fontSize: '11px', marginTop: 4 }}>
                      {item.type === 'call_note' ? 'Call note' : 'Note'} · {timeAgo(item.created_at)}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Shared styles ────────────────────────────────────────────────────────────

const inputStyle = {
  width: '100%', padding: '8px 12px', borderRadius: '8px',
  border: '1px solid #d1d5db', fontSize: '13px', boxSizing: 'border-box',
  fontFamily: 'inherit',
};

const emptyStyle = {
  color: '#9ca3af', textAlign: 'center', padding: '30px 0', fontSize: '13px',
};

function btnStyle(hex) {
  return {
    background: `#${hex}`, color: '#fff', border: 'none',
    borderRadius: '8px', padding: '7px 16px', fontSize: '13px',
    cursor: 'pointer', fontWeight: 600,
  };
}

// ── Main CrmPage export ──────────────────────────────────────────────────────

export default function CrmPage({ onDial }) {
  const [selected, setSelected] = useState(null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#fff', overflow: 'hidden' }}>
      {selected ? (
        <CustomerDetail
          customer={selected}
          onBack={() => setSelected(null)}
          onDial={onDial}
        />
      ) : (
        <CustomerList
          onSelect={setSelected}
          onDial={onDial}
        />
      )}
    </div>
  );
}
